import { capitalizeWords } from "@rebuild/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PhotoGalleryTile } from "./PhotoGalleryTile";
import type { PhotoGalleryGroup, PhotoGalleryItem } from "./photos.types";
import {
  formatPhotoNumberOverlayTimestamp,
  formatPhotoTime,
  getPhotoCapturedAtMs,
  splitPhotosByCapturedDay,
} from "./photos.utils";

const INITIAL_PHOTO_COUNT = 24;
const PHOTO_BATCH_SIZE = 24;

interface PhotoGalleryProps {
  groups: PhotoGalleryGroup[];
  onSelect: (photo: PhotoGalleryItem) => void;
  hideRoomNames?: boolean;
  currentRoomId?: string;
  // Retained as an optional compatibility prop for existing gallery callers.
  scrollPositionRef?: { current: number };
  selectedIds?: Set<string>;
  onToggleSelect?: (photo: PhotoGalleryItem) => void;
  onSelectAllInGroup?: (photos: PhotoGalleryItem[], select: boolean) => void;
  /** Expects each group's photos already sorted by capture time (see sortGroupPhotosByCapturedAt). */
  showDateSubheaders?: boolean;
}

export function PhotoGallery({
  groups,
  onSelect,
  hideRoomNames,
  currentRoomId,
  selectedIds,
  onToggleSelect,
  onSelectAllInGroup,
  showDateSubheaders,
}: PhotoGalleryProps) {
  const componentRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const autoScrolledRoomIdRef = useRef<string | undefined>(undefined);
  const isSelectionActive = Boolean(selectedIds && selectedIds.size > 0);
  const totalPhotoCount = useMemo(() => groups.reduce((total, group) => total + group.photos.length, 0), [groups]);
  const groupsById = useMemo(() => new Map(groups.map((group) => [group.id, group])), [groups]);
  const initialMountedPhotoCount = useMemo(() => {
    if (!currentRoomId) {
      return INITIAL_PHOTO_COUNT;
    }

    let photoCountBeforeCurrentRoom = 0;
    for (const group of groups) {
      if (group.id === currentRoomId) {
        const firstCurrentRoomPhotoCount = group.photos.length > 0 ? 1 : 0;
        return Math.max(INITIAL_PHOTO_COUNT, photoCountBeforeCurrentRoom + firstCurrentRoomPhotoCount);
      }
      photoCountBeforeCurrentRoom += group.photos.length;
    }

    return INITIAL_PHOTO_COUNT;
  }, [currentRoomId, groups]);
  const [mountedPhotoCount, setMountedPhotoCount] = useState(() => Math.min(totalPhotoCount, initialMountedPhotoCount));
  const visibleGroups = useMemo(() => {
    let remainingPhotoCount = mountedPhotoCount;
    return groups
      .map((group) => {
        const photos = group.photos.slice(0, Math.max(remainingPhotoCount, 0));
        remainingPhotoCount -= photos.length;
        return { ...group, photos };
      })
      .filter((group) => group.photos.length > 0);
  }, [groups, mountedPhotoCount]);

  useEffect(() => {
    setMountedPhotoCount((currentCount) => Math.min(totalPhotoCount, Math.max(currentCount, initialMountedPhotoCount)));
  }, [initialMountedPhotoCount, totalPhotoCount]);

  const loadNextPhotoBatch = useCallback(() => {
    setMountedPhotoCount((currentCount) => Math.min(totalPhotoCount, currentCount + PHOTO_BATCH_SIZE));
  }, [totalPhotoCount]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || mountedPhotoCount >= totalPhotoCount || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          loadNextPhotoBatch();
        }
      },
      { rootMargin: "0px 0px 600px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadNextPhotoBatch, totalPhotoCount, mountedPhotoCount]);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (!currentRoomId || autoScrolledRoomIdRef.current === currentRoomId) {
        return;
      }
      const selectedRoom = componentRefs.current?.[currentRoomId];
      if (selectedRoom) {
        selectedRoom.scrollIntoView({ behavior: "smooth", block: "start" });
        autoScrolledRoomIdRef.current = currentRoomId;
      }
    });
  }, [currentRoomId, groups]);

  return (
    <div className="flex flex-wrap p-4">
      {visibleGroups.map((group) => {
        const sourceGroup = groupsById.get(group.id) ?? group;
        const allSelected = sourceGroup.photos.every((photo) => selectedIds?.has(photo.galleryId));
        const showSelectAll = Boolean(onToggleSelect && onSelectAllInGroup && sourceGroup.photos.length > 0);
        const hasDayRuns =
          Boolean(showDateSubheaders) && sourceGroup.photos.some((photo) => getPhotoCapturedAtMs(photo) !== undefined);
        const dayRuns = hasDayRuns ? splitPhotosByCapturedDay(group.photos) : [{ key: group.id, photos: group.photos }];

        return (
          <div
            id={group.id}
            key={group.id}
            className="mb-8"
            style={{ contentVisibility: "auto", containIntrinsicSize: "auto 240px" }}
            ref={(el) => {
              if (el) {
                componentRefs.current[group.id] = el;
              }
            }}
          >
            {!hideRoomNames && group.title !== undefined && (
              <div className="mb-4 flex items-center gap-3">
                {group.title !== undefined && (
                  <h3 className={`text-xl font-semibold ${group.isUnassignedOrMappedByUser ? "text-gray-400 italic" : "text-gray-800"}`}>
                    {capitalizeWords(group.title)}
                  </h3>
                )}
                {showSelectAll && (
                  <button
                    type="button"
                    className="text-sm text-indigo-600 hover:text-indigo-700"
                    onClick={() => onSelectAllInGroup?.(sourceGroup.photos, !allSelected)}
                  >
                    {allSelected ? "Deselect all" : "Select all in room"}
                  </button>
                )}
              </div>
            )}
            {dayRuns.map((run) => (
              <div key={run.key} className={hasDayRuns ? "mb-6 last:mb-0" : undefined}>
                {hasDayRuns && <h4 className="mb-2 text-sm font-medium text-gray-600">{run.label ?? "Date unknown"}</h4>}
                <div className="flex flex-wrap gap-4">
                  {run.photos.map((photo) => {
                    const altText = getPhotoAltText(photo, group.title);
                    const isSelected = Boolean(selectedIds?.has(photo.galleryId));
                    const capturedAt = getPhotoCapturedAtMs(photo);

                    return (
                      <PhotoGalleryTile
                        key={photo.galleryId}
                        photo={photo}
                        altText={altText}
                        isSelected={isSelected}
                        isSelectionActive={isSelectionActive}
                        onSelect={onSelect}
                        onToggleSelect={onToggleSelect}
                        timestampLabel={hasDayRuns ? formatPhotoTime(capturedAt) : formatPhotoNumberOverlayTimestamp(capturedAt)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );
      })}
      {mountedPhotoCount < totalPhotoCount && (
        <div ref={loadMoreSentinelRef} className="basis-full py-4 text-center">
          <button type="button" className="text-sm text-indigo-600 hover:text-indigo-700" onClick={loadNextPhotoBatch}>
            Load more photos
          </button>
        </div>
      )}
    </div>
  );
}

function getPhotoAltText(photo: PhotoGalleryItem, groupTitle?: string): string {
  const roomName = photo.roomName || groupTitle;
  return roomName ? `${capitalizeWords(roomName)} photo` : "Estimate photo";
}
